import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/adornisEntity.js';
import type { Subject } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { ExternerMitarbeiter } from '../master-data-management/users/entities/ExternerMitarbeiter.js';
import { Course } from './Course.js';

/**
 * @enum CourseAbschlussType
 *
 * The type of a course of the CHANCENabschluss
 */
export enum CourseAbschlussType {
  HAUPTSCHULABSCHLUSS = 'Hauptschulabschluss',
  MITTLERER_BILDUNGSABSCHLUSS = 'Mittlerer Bildungsabschluss',
  EF = 'EF',
  ABITUR = 'Abitur',
  FACHHOCHSCHULREIFE = 'Fachhochschulreife',
}

/**
 * @entity CourseAbschluss
 *
 * A course of the CHANCENabschluss
 */
@Entity()
export class CourseAbschluss extends Course {
  static override _class = 'CourseAbschluss';

  constructor(doc: PartialEntityData<CourseAbschluss>) {
    super(doc);
  }

  @Field(() => DateTime)
  endDate: Maybe<DateTime>;

  @Field(() => String)
  diKuLink: Maybe<string>;

  @DataLoadedField(() => [ExternerMitarbeiter], ({ diKuIDs }) => diKuIDs ?? [])
  diKus: Maybe<ExternerMitarbeiter[]>;

  /**
   * The IDs of the DigitaleKursleitung that are part of this course
   */
  @Field(() => [String], { default: v => v ?? [] })
  diKuIDs: Maybe<string[]>;

  /**
   * Whether the course is open to all KPFs
   */
  @Field(() => Boolean)
  openToAll: Maybe<boolean>;

  /**
   * The subject of the course
   */
  @Field(() => String)
  subject: Maybe<Subject>;

  /**
   * The type of the course, like "Hauptschulabschluss" or "Abitur"
   */
  @Field(() => String)
  type: Maybe<CourseAbschlussType>;

  /**
   * The target class for the course (Jahrgangsstufe)
   */
  @Field(() => Int)
  class: Maybe<number>;

  /**
   * The maximum number of attendees for the course
   */
  @Field(() => Int)
  maxAttendanceCount: Maybe<number>;

  /**
   * If changes to the course were made and a notification should be shown
   */
  @Field(() => Boolean, { default: v => v ?? false })
  changeNotification?: boolean;
}
